import { ColumnOutlined } from '@vindr/icons';
import { DefinedColumns } from '@vindr/services/interfaces';
import { Pick, addToArray, getArrayVal, sortAlphaText } from '@vindr/utils/helpers/common.helper';
import cx from '@vindr/utils/helpers/cx';
import { localStorageAction } from '@vindr/utils/helpers/storage';
import useCalcTableScroll from '@vindr/utils/hooks/useCalcTableScroll';
import { isIOS } from '@vindr/utils/mobile';
import {
  Table as AntTable,
  Button,
  Checkbox,
  ConfigProvider,
  Empty,
  MenuRef,
  TableProps,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import type { DefaultRecordType } from 'rc-table/lib/interface';
import { Dispatch, SetStateAction, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDragResizeColumn from '../../hooks/useDragResizeColumn';
import PopupContextMenu from '../ui/PopupContextMenu';
import ActionIcon from './ActionIcon/ActionIcon';
import PaginationTable from './PaginationTable';
import Popover from './Popover';
import { ContextMenuState, MenuPopup } from './PopupContextMenu/PopupContextMenu';
import { useThemeContext } from './ThemeProvider';
import './Table.css';

type Columns<T> = DefinedColumns<T> | any;

export interface VindrTableProps<T extends Record<string, any>>
  extends Omit<TableProps<T>, 'pagination' | 'onChange' | 'dataSource'> {
  columns: TableProps<T>['columns'];
  scroll?: TableProps<T>['scroll'] & { minY?: number };
  rowKey?: string | ((record: T, idx: number) => string);
  loading?: boolean;
  pagination?: false | null | PaginationProps;
  contextMenu?: MenuPopup<T> | false;
  triggerSubMenuAction?: 'hover' | 'click';
  count?: number;
  onTableChange?: TableProps<T>['onChange'];
  onPaginationChange?: (pagination: VindrTableProps<T>['pagination']) => void;
  onRow?: TableProps<T>['onRow'];
  className?: string;
  selectedRowKeys?: T[];
  setSelectedRowKeys?:
    | Dispatch<SetStateAction<VindrTableProps<T>['selectedRowKeys']>>
    | ((records: T[]) => void)
    | undefined;
  wrapperClassName?: string | null | undefined;
  dataSource?: T[];
  style?: React.CSSProperties;
  noDataText?: string | React.ReactNode;
  rowCustom?: Pick<
    ReturnType<typeof useDragResizeColumn>,
    'getDefaultCol' | 'resetColumns' | 'setVisibleColumns' | 'visibleColumns'
  > & {
    performedColumns: Parameters<typeof useDragResizeColumn>['0'];
    settingKey: Parameters<typeof useDragResizeColumn>['1'];
  };
}

type TypeOnRow = ReturnType<Parameters<typeof Table>['0']['onRow']>;

export interface PaginationProps {
  page: number;
  size: 25 | 50 | 100 | number;
}

let mouseStartTime = 0;
const DOUBLE_CLICK_THRESHOLD_MS = 500;

const Table = <T extends DefaultRecordType>({
  className = '',
  columns,
  contextMenu = false,
  count = 0,
  dataSource,
  loading,
  noDataText = 'No Data',
  onPaginationChange,
  onRow,
  onTableChange,
  pagination = { page: 1, size: 25 },
  rowCustom,
  rowKey = 'id',
  rowSelection,
  scroll,
  selectedRowKeys,
  setSelectedRowKeys,
  style,
  triggerSubMenuAction = 'hover',
  wrapperClassName = 'table-wrapper',
  ...rest
}: VindrTableProps<T>): JSX.Element => {
  const { theme } = useThemeContext();
  const { t } = useTranslation('Vindoc');
  const { scroll: calcTableScroll } = useCalcTableScroll({ className: wrapperClassName });

  const {
    getDefaultCol,
    performedColumns,
    resetColumns,
    setVisibleColumns,
    settingKey,
    visibleColumns,
  } = rowCustom || {};

  const { handleTouchEnd, handleTouchStart } = useTouchHandlers();
  const { selectedRow, setSelectedRow, handleClick } = useRowSelection<T>(
    selectedRowKeys,
    setSelectedRowKeys
  );
  const {
    contextMenuRef,
    contextMenuState,
    setContextMenuState,
    handleContextMenu,
    contextMenuProps,
  } = useContextMenu(contextMenu, getArrayVal(selectedRow), setSelectedRow);

  const handlePaginationChange = useCallback(
    (page, pageSize) => {
      onPaginationChange({ page, size: pageSize });
    },
    [onPaginationChange]
  );

  const showHideColumns = useCallback(
    (resetColumns): ColumnsType<T>[0] =>
      rowCustom
        ? {
            title: (
              <Popover
                placement="bottomRight"
                title={t('Modify Columns')}
                trigger="click"
                content={
                  <ColumnsContent<T>
                    {...{
                      getDefaultCol,
                      performedColumns,
                      resetColumns,
                      setVisibleColumns,
                      settingKey,
                      visibleColumns,
                    }}
                  />
                }
              >
                <Button size="small">
                  <ColumnOutlined className={'text-xl'} color={'white'} />
                </Button>
              </Popover>
            ),
            key: 'showHideColumns',
            fixed: 'right',
            align: 'center',
            width: 50,
            render: (record) => {
              if (typeof setContextMenuState !== 'function') return null;
              return (
                <div className="flex justify-center">
                  <ActionIcon.Options
                    style={{ border: 'none' }}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setContextMenuState({ visible: true, x: e.clientX, y: e.clientY });
                      setSelectedRow?.([record]);
                    }}
                  />
                </div>
              );
            },
          }
        : {},
    [
      getDefaultCol,
      performedColumns,
      rowCustom,
      setContextMenuState,
      setSelectedRow,
      setVisibleColumns,
      settingKey,
      t,
      visibleColumns,
    ]
  );

  const customColumns = useMemo(
    () => (rowCustom ? [...columns, showHideColumns(resetColumns)] : columns),
    [columns, resetColumns, rowCustom, showHideColumns]
  ) satisfies ColumnsType<T>;

  const hasActionColumn = useMemo(() => customColumns.at(-1)?.fixed, [customColumns]);

  const getRowProps = (record: T): TypeOnRow => {
    if (!(onRow || contextMenu)) {
      if (hasActionColumn) return;
      return { onClick: () => setSelectedRow(addToArray(record)) };
    }

    const onContextMenu: TypeOnRow['onContextMenu'] = contextMenu
      ? (event: React.MouseEvent | React.TouchEvent) => handleContextMenu({ event, record })
      : undefined;

    return {
      ...onRow?.(record),
      onClick: (event: React.MouseEvent) => handleClick({ event, record, onRow }),
      ...(isIOS()
        ? {
            onTouchStart: handleTouchStart,
            onTouchEnd: (event: React.TouchEvent) => handleTouchEnd({ event, onContextMenu }),
            onTouchMove: handleTouchStart,
          }
        : { onContextMenu }),
    };
  };

  return (
    <>
      <div className={cx(`h-0 flex-1 overflow-hidden`, wrapperClassName)}>
        <ConfigProvider
          theme={{ token: { colorBgContainer: theme === 'dark' ? '#2c5166' : '#eaeaea' } }}
        >
          <AntTable<T>
            className={cx(className)}
            showSorterTooltip={false}
            size="small"
            {...{ rowSelection, rowKey, loading, style }}
            columns={customColumns}
            dataSource={dataSource as any}
            pagination={false}
            rowClassName={(record) => (find(selectedRow, record) ? 'selected bg-[#2C4654]' : '')}
            scroll={{ y: scroll?.y || calcTableScroll?.y, x: scroll?.x || 'none' }}
            locale={{
              emptyText: <Empty description={typeof noDataText === 'string' ? t(noDataText) : noDataText} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
            }}
            onChange={onTableChange}
            onRow={getRowProps}
            {...rest}
          />
        </ConfigProvider>
      </div>
      {contextMenu &&
      contextMenuProps &&
      typeof contextMenuProps === 'object' &&
      contextMenuProps?.items?.length ? (
        <PopupContextMenu
          ref={contextMenuRef}
          menu={contextMenuProps}
          triggerSubMenuAction={triggerSubMenuAction}
          {...contextMenuState}
          currentRecord={getArrayVal(selectedRow)}
          setContextMenuState={setContextMenuState}
          setSelectedRow={setSelectedRow}
        />
      ) : null}
      {pagination && (
        <div className="mt-3 flex w-full justify-end">
          <PaginationTable
            page={pagination?.page}
            size={pagination?.size}
            totalElements={count}
            onChange={handlePaginationChange}
          />
        </div>
      )}
    </>
  );
};

const arePropsEqual = (prevProps: VindrTableProps<any>, nextProps: VindrTableProps<any>) => {
  const propsToCompare: (keyof VindrTableProps<any>)[] = [
    'dataSource',
    'loading',
    'pagination',
    'scroll',
    'columns',
    'count',
    'contextMenu',
    'noDataText',
    'components',
    'rowSelection',
    'rowCustom',
  ];

  return propsToCompare.every((prop) => isEqual(prevProps[prop], nextProps[prop]));
};

export default memo(Table, arePropsEqual) as typeof Table;

export const useContextMenu = <T extends Record<string, any>>(
  contextMenu: MenuPopup<T> | false,
  selectedRow: T,
  setSelectedRow?: VindrTableProps<T>['setSelectedRowKeys']
) => {
  const contextMenuRef = useRef<MenuRef>();
  const [contextMenuState, setContextMenuState] = useState<
    Omit<ContextMenuState<T>, 'currentRecord'>
  >({
    visible: false,
    x: 0,
    y: 0,
  });

  const handleContextMenu = useCallback(
    ({ event, record }: { event: React.MouseEvent | React.TouchEvent; record: T }) => {
      event.stopPropagation();
      event.preventDefault();

      const handleOutsideClick = (e: MouseEvent) => {
        if (!contextMenuRef?.current) return;
        const childNodes = contextMenuRef?.current?.menu?.list?.childNodes;
        if (
          contextMenuRef?.current &&
          !childNodes?.contains?.(e.target) &&
          contextMenuState.visible
        ) {
          setContextMenuState((prev) => ({ ...prev, visible: false }));
          document.removeEventListener('click', handleOutsideClick);
        }
      };

      if (!contextMenuState.visible) {
        document.addEventListener('click', handleOutsideClick);
      }

      setSelectedRow?.([record]);
      setContextMenuState({
        visible: true,
        x: 'clientX' in event ? event.clientX : event.changedTouches?.[0].clientX,
        y: 'clientY' in event ? event.clientY : event.changedTouches?.[0].clientY,
      });
    },
    [contextMenuState.visible, setSelectedRow]
  );

  const contextMenuProps = (
    contextMenu
      ? {
          ...contextMenu,
          onClick: (it) => {
            contextMenu.onClick(it, getArrayVal(selectedRow));
            setContextMenuState((prev) => ({ ...prev, visible: false }));
          },
        }
      : false
  ) satisfies MenuPopup<T> | false;

  return {
    contextMenuRef,
    contextMenuState,
    setContextMenuState,
    handleContextMenu,
    contextMenuProps,
  };
};

export const useTouchHandlers = () => {
  const handleTouchStart = useCallback(() => {
    mouseStartTime = new Date().getTime();
  }, []);

  const handleTouchEnd = useCallback(
    ({
      event,
      onContextMenu,
    }: {
      event: React.TouchEvent;
      onContextMenu?: (event: any) => void;
    }) => {
      const mouseEndTime = new Date().getTime();
      if (
        typeof onContextMenu === 'function' &&
        mouseEndTime - mouseStartTime >= DOUBLE_CLICK_THRESHOLD_MS
      ) {
        onContextMenu(event);
      }
    },
    []
  );

  return { handleTouchEnd, handleTouchStart };
};

export const useRowSelection = <T extends Record<string, any>>(
  selectedRowProps: VindrTableProps<T>['selectedRowKeys'],
  setSelectedRowProps: VindrTableProps<T>['setSelectedRowKeys']
) => {
  const [selectedRow, setSelectedRow] = useState<VindrTableProps<T>['selectedRowKeys']>([]);
  const lastClickRef = useRef<{ record: T | null; time: number }>({
    record: null,
    time: 0,
  });

  if (!selectedRowProps && typeof setSelectedRowProps !== 'function') {
    [selectedRowProps, setSelectedRowProps] = [selectedRow, setSelectedRow];
  }

  const handleClick = useCallback(
    ({
      event,
      record,
      onRow,
    }: {
      event: React.MouseEvent;
      record: T;
      onRow?: TableProps<T>['onRow'];
    }) => {
      const currentTime = new Date().getTime();
      const timeDiff = currentTime - lastClickRef.current.time;
      const isSameRecord = lastClickRef.current.record
        ? isEqual(lastClickRef.current.record, record)
        : false;
      const isDoubleClick = isSameRecord && timeDiff <= DOUBLE_CLICK_THRESHOLD_MS;

      // Always update selected row
      setSelectedRowProps?.(addToArray(record));

      if (isDoubleClick && onRow?.(record)?.onDoubleClick) {
        onRow(record)?.onDoubleClick(event);
      } else if (onRow?.(record)?.onClick) {
        onRow(record)?.onClick(event);
      }

      // Always update last click info
      lastClickRef.current = { record, time: currentTime };
    },
    [setSelectedRowProps]
  );

  return { selectedRow: selectedRowProps, setSelectedRow: setSelectedRowProps, handleClick };
};

const ColumnsContent = <T,>({
  getDefaultCol,
  performedColumns,
  resetColumns,
  setVisibleColumns,
  settingKey,
  visibleColumns,
}: Pick<
  ReturnType<typeof useDragResizeColumn>,
  'getDefaultCol' | 'resetColumns' | 'setVisibleColumns' | 'visibleColumns'
> & {
  performedColumns: Parameters<typeof useDragResizeColumn>['0'];
  settingKey: Parameters<typeof useDragResizeColumn>['1'];
}) => {
  const { t } = useTranslation('Vindoc');

  const handleResetColumns = useCallback(() => {
    const defaultCol = getDefaultCol();
    const sortedPerformedColumns = sortCol(defaultCol, performedColumns);
    const columnsToSet = defaultCol || getPerformedColumns(sortedPerformedColumns);

    // columnsToSet.push(Pick(columnsToSet, ['key', 'dataIndex', 'width']));

    localStorageAction.set(settingKey, { resizableColumns: columnsToSet });
    setVisibleColumns(columnsToSet); //? Reset position column

    resetColumns(); //? Reset width column
  }, [getDefaultCol, settingKey, setVisibleColumns, resetColumns]);

  const handleVisibleChange = useCallback(
    <T,>(column: Columns<T>[number]) => {
      const isColumnVisible = visibleColumns?.some((visCol) => visCol.key === column.key);
      const updatedVisibleColumns = isColumnVisible
        ? visibleColumns.filter((visCol) => visCol.key !== column.key)
        : [...visibleColumns, column];

      const moveEmptyToLast = updatedVisibleColumns.sort((a, b) => {
        if (a.dataIndex === 'empty' && a.key === 'empty') return 1; // Move 'empty' to the end
        if (b.dataIndex === 'empty' && b.key === 'empty') return -1; // Move 'empty' to the end
        return 0; // No change in order
      });

      setVisibleColumns(moveEmptyToLast);
      localStorageAction.set(settingKey, { resizableColumns: moveEmptyToLast });
    },
    [setVisibleColumns, settingKey, visibleColumns]
  );

  return (
    <div className="flex flex-col" style={{ maxHeight: 'min(400px, 80vh)' }}>
      <div className="flex-1" style={{ overflowY: 'auto' }}>
        {sortCol(visibleColumns, performedColumns).map((column) => (
          <div key={column.title}>
            <Checkbox
              checked={visibleColumns?.some((col) => col.key === column.key)}
              disabled={column?.lock}
              onChange={() => handleVisibleChange<T>(column)}
            >
              {column.title}
            </Checkbox>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <Button className="reset-button" size="small" onClick={handleResetColumns}>
          {t('Reset')}
        </Button>
        <div>{`${visibleColumns.filter((x) => x.key !== 'empty')?.length} / ${
          performedColumns?.length
        }`}</div>
      </div>
    </div>
  );
};

function sortCol(savedColumns, performedColumns) {
  savedColumns = addToArray(savedColumns, []);
  performedColumns = addToArray(performedColumns, []);

  const savedKeys = savedColumns.map((column) => column.key);
  performedColumns = performedColumns?.length
    ? sortAlphaText(performedColumns, 'asc', (item) => item?.title || '')
    : performedColumns;
  return performedColumns.sort((a, b) => {
    const indexA = savedKeys.indexOf(a.key);
    const indexB = savedKeys.indexOf(b.key);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export function getPerformedColumns<T>(performedColumns): Columns<T> {
  return performedColumns.map(PickToLocalStorage);
}

export function PickToLocalStorage(column) {
  return Pick(column, ['title', 'key', 'dataIndex', 'width']);
}
